import { CircularProgress } from '@mui/material';
import { Card } from 'components/tailwind-ui/Card';
import { PresetView, usePreset } from 'pages/Shipments/Containers/types';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { currencyFormatterNoCents } from 'utils/container-transforms';
import { formatNumber } from 'utils/common';
import { KeyMetric, Stat } from 'pages/Shipments/HistoricalPerDiem/components/KeyMetric';
import { SectionTitle } from '../SectionTitle';
import { useTrackSegment } from '../hooks';
import { CardTitle } from './StatusCards/CardTitle';

interface Props {
  /** The section title */
  title: string;
  /** The destination upon click */
  presetView: PresetView;
  /** The amount in cents */
  amount: number;
  /** Number of containers */
  containers: number;
  /** Whether we should show a loading spinner */
  loading?: boolean;
  /** Whether we should indicate an error occurred */
  error?: boolean;
  /** The stat data passed into the card */
  stats: Stat[];
  /** The text to display in the card */
  text: string;
  tooltip?: string;
}

export const AlertCard: React.FC<Props> = (props) => {
  const { amount, title, containers, loading, error, presetView, text, stats, tooltip } = props;

  const currency = currencyFormatterNoCents.format(amount / 100);

  const formattedContainers = formatNumber(containers);

  let currencyText = currency;
  let inner = (
    <>
      <div className="flex">
        <div className="flex items-start">
          <CardTitle
            textColor="text-darkred-800"
            backgroundColor="bg-red-50"
            text={text}
            count={formattedContainers}
          />
        </div>
      </div>
      <div className="grid grid-cols-4">
        {stats.map((stat: Stat) => (
          <KeyMetric key={stat.title} {...stat} />
        ))}
      </div>
    </>
  );
  if (loading) {
    currencyText = 'Loading...';
    inner = (
      <div className="flex justify-center">
        <CircularProgress size={40} className="my-2" />
      </div>
    );
  } else if (error) {
    currencyText = 'Error';
    inner = <h1 className="text-red-900 font-bold">Unavailable</h1>;
  }

  const presetLink = usePreset(presetView).to;
  const handleClick = useTrackSegment(presetView, 'shipments-dashboard-alert-card');

  return (
    <div className="flex flex-col space-y-4">
      <SectionTitle title={title} tooltip={tooltip} value={currencyText} />
      <Card className="text-gray-500 p-4 group !bg-blue-100 hover:border-blue-900 hover:shadow-md transition-all">
        <Link to={presetLink} onClick={handleClick} className="flex flex-col space-y-4">
          {inner}
        </Link>
      </Card>
    </div>
  );
};
